import { TreeNode } from '../utils/tree';
import { flattenChapters } from './BookOutline';

export interface Chapter {
  id: number;
  doc: string;
  label: string;
  pageId: number;
  chapters?: Chapter[];
  position?: number;
  anchor?: string;
  index?: number;
  cw?: boolean;
}

export type ChapterData = Omit<Chapter, 'chapters' | 'id'>;

export type ChapterNode = TreeNode<ChapterData>;

export const toChapterNode = (chapter: Chapter): ChapterNode => {
  const { id, chapters, ...rest } = chapter;
  return {
    id,
    data: rest,
    children: chapters?.map(toChapterNode),
  };
};
export const getNextFocusChapter = (
  chapters: Chapter[],
  current: Chapter,
  isOpen: boolean
): Chapter => {
  if (isOpen && current.chapters && current.chapters.length > 0) {
    return current.chapters[0];
  }

  const parentChapter = getParentChapter(chapters, current);

  if (parentChapter) {
    const siblingChapters = parentChapter.chapters!;

    const index = siblingChapters.findIndex((chapter) => chapter.id === current.id);

    if (index >= 0 && index < siblingChapters.length - 1) {
      return siblingChapters[index + 1];
    }

    const grandparentChapter = getParentChapter(chapters, parentChapter);
    if (grandparentChapter) {
      const grandparentIndex = grandparentChapter.chapters!.findIndex(
        (chapter) => chapter.id === parentChapter.id
      );
      if (grandparentIndex >= 0 && grandparentIndex < grandparentChapter.chapters!.length - 1) {
        return grandparentChapter.chapters![grandparentIndex + 1];
      }
    }

    const topIndex = chapters.findIndex((chapter) => chapter.id === parentChapter.id);
    if (topIndex >= 0 && topIndex < chapters.length - 1) {
      return chapters[topIndex + 1];
    }

    return current;
  } else {
    const index = chapters.findIndex((chapter) => chapter.id === current.id);
    if (index >= 0 && index < chapters.length - 1) {
      return chapters[index + 1];
    }

    return current;
  }
};

export const getPreviousFocusChapter = (
  chapters: Chapter[],
  current: Chapter,
  openIds: string[] = []
) => {
  const parentChapter = getParentChapter(chapters, current);

  if (parentChapter) {
    const siblingChapters = parentChapter.chapters ?? [];
    const index = siblingChapters.findIndex((chapter) => chapter.id === current.id);
    if (index > 0) {
      const siblingChapter = siblingChapters[index - 1];
      if (siblingChapter && openIds.includes(String(siblingChapter.id))) {
        return siblingChapter.chapters?.[siblingChapter.chapters.length - 1];
      } else {
        return siblingChapter;
      }
    } else if (index === 0) {
      // If the current chapter is the first sibling, return the parent chapter
      return parentChapter;
    }

    const grandparentChapter = getParentChapter(chapters, parentChapter);
    if (grandparentChapter) {
      const grandparentIndex = grandparentChapter.chapters!.findIndex(
        (chapter) => chapter.id === parentChapter.id
      );
      if (grandparentIndex > 0) {
        return grandparentChapter.chapters![grandparentIndex - 1];
      }
    }

    return parentChapter;
  } else {
    const index = chapters.findIndex((chapter) => chapter.id === current.id);

    if (index > 0) {
      const siblingChapter = chapters[index - 1];
      if (openIds.includes(String(siblingChapter.id))) {
        return siblingChapter.chapters![siblingChapter.chapters!.length - 1];
      } else {
        return siblingChapter;
      }
    }

    return current;
  }
};

export const getParentChapter = (chapters: Chapter[], chapterIn: Chapter): Chapter | undefined => {
  const flat = flattenChapters(chapters);
  return flat.find(
    (chapter) =>
      chapter?.chapters &&
      chapter.chapters.findIndex((chapter: Chapter) => {
        return chapter.id === chapterIn.id;
      }) >= 0
  );
};

export const getAllParentIdsForChapter = (chapter: Chapter) => {
  const ids = [chapter.id];

  if (chapter?.chapters) {
    for (const subChapter of chapter.chapters) {
      if (subChapter.chapters && subChapter.chapters.length > 0) {
        ids.push(...getAllParentIdsForChapter(subChapter));
      }
    }
  }

  return ids;
};

export const getAllIdsForChapter = (chapter: Chapter) => {
  const ids = [chapter.id];

  if (chapter?.chapters) {
    for (const subChapter of chapter.chapters) {
      ids.push(...getAllIdsForChapter(subChapter));
    }
  }

  return ids;
};

export const displayChapter = (
  chapter: Chapter,
  options?: { index?: number; maxLength?: number }
): string => {
  const cleaned = `${options?.index ? `${options.index} - ` : ''}${chapter?.label ? cleanLabel(chapter?.label) : ''}`;
  if (options?.maxLength) {
    return cleaned.length > options.maxLength
      ? `${cleaned.substring(0, options.maxLength)}...`
      : cleaned;
  }

  return cleaned;
};

export const getLineage = (chapter: Chapter): Chapter[] => {
  const output = [chapter];

  const allChapters = flattenChapters(chapter.chapters ?? []);

  return [...output, ...allChapters];
};

const cleanLabel = (labelIn: string): string => {
  let label = `${labelIn}`;
  //Logger.log(`Chapter cleanLabel: ${labelIn}`);
  if (!label || label.length === 0) {
    return '';
  }
  const firstChar = label?.charAt(0) ?? '';
  const lastChar = label?.charAt(label.length - 1) ?? '';
  if (firstChar && (firstChar === '{' || firstChar === '(' || firstChar === '[')) {
    label = label.substring(1);
  }
  if (lastChar && (lastChar === '}' || lastChar === ')' || lastChar === ']')) {
    label = label.substring(0, label.length - 1);
  }
  return label;
};

export const getEndChapterId = (chapterNumber: number) => {
  return `cwai-c-end-${chapterNumber}`;
};

export const getChapterId = (chapterId: number) => {
  return `cwai-chapter-${chapterId}`;
};

export const getTopLevelChapter = (
  haystackChapters: Chapter[],
  needleChapter: Chapter
): Chapter | null => {
  for (const haystackChapter of haystackChapters) {
    if (isChapterAncestor(haystackChapter, needleChapter)) {
      return haystackChapter;
    }
  }

  return null;
};

export const isChapterAncestor = (
  parentChapter: Chapter,
  childChapter: Chapter | null
): boolean => {
  //Logger.log(`isChapterAncestor: ${parentChapter.id} ${childChapter}`);

  if (!childChapter || !parentChapter) {
    return false;
  }

  if (parentChapter.id === childChapter.id) {
    return true;
  }

  if (parentChapter?.chapters) {
    for (const parentChildChapter of parentChapter.chapters) {
      if (isChapterAncestor(parentChildChapter, childChapter)) {
        return true;
      }
    }
  }

  return false;
};

export const getPreviousChapter = (chapters: Chapter[], id: number | undefined): Chapter | null => {
  if (!id) {
    return null;
  }

  let index = id;
  while (index >= 0) {
    index--;
    const chapter = getChapterById(chapters, index);
    if (chapter) {
      return chapter;
    }
  }

  return null;
};

export const getNextChapter = (chapters: Chapter[], id: number | undefined): Chapter | null => {
  if (!id) {
    return null;
  }

  let index = id;
  const maxId = chapters[chapters.length - 1].id;
  while (index <= maxId) {
    index++;
    const chapter = getChapterById(chapters, index);
    if (chapter) {
      return chapter;
    }
  }

  return null;
};

export const getChaptersAtLevel = (
  chapters: Chapter[],
  level: number,
  currentLevel: number = 0
): Chapter[] => {
  if (currentLevel === level) {
    return chapters;
  } else {
    let subChapters: Chapter[] = [];
    for (const chapter of chapters) {
      if (chapter?.chapters) {
        // Add chapters to subChapters
        subChapters = subChapters.concat(chapter.chapters);
      }
    }

    if (subChapters.length === 0) {
      return [];
    }

    return getChaptersAtLevel(subChapters, level, currentLevel + 1);
  }
};

export const getChapterById = (chapters: Chapter[], id: number): Chapter | null => {
  for (const chapter of chapters) {
    if (chapter.id === id) {
      return chapter;
    }

    if (chapter?.chapters) {
      const found = getChapterById(chapter.chapters, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const chapterCwPredicate = (chapter: Chapter) => !!chapter.cw;
